import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface Benefit {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const ProductBenefits: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const benefitRefs = useRef<(HTMLDivElement | null)[]>([]);
  const scrollTriggerInstances = useRef<ScrollTrigger[]>([]);

  const benefits: Benefit[] = [
    {
      id: "quality",
      title: "Premium Quality",
      description:
        "Our products undergo rigorous testing for potency, purity, and consistency, ensuring you receive only the highest quality cannabis.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
          />
        </svg>
      ),
    },
    {
      id: "variety",
      title: "Diverse Selection",
      description:
        "From classic strains to innovative products, our extensive range caters to both recreational users and those seeking specific therapeutic effects.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
      ),
    },
    {
      id: "expert-guidance",
      title: "Expert Guidance",
      description:
        "Our knowledgeable staff provides personalized recommendations based on your preferences and desired effects.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
          />
        </svg>
      ),
    },
    {
      id: "responsible",
      title: "Responsible Enjoyment",
      description:
        "We promote educated and mindful consumption, providing information on dosing, onset times, and duration of effects.",
      icon: (
        <svg
          className="w-10 h-10"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  ];

  useEffect(() => {
    // Store our ScrollTrigger instances to clean up only those we created
    const createdTriggers: ScrollTrigger[] = [];

    if (titleRef.current) {
      const titleTrigger = ScrollTrigger.create({
        id: "product-benefits-title", // Add unique ID
        trigger: titleRef.current,
        start: "top 80%",
        once: true,
      });

      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 0.7,
          scrollTrigger: titleTrigger,
        }
      );

      createdTriggers.push(titleTrigger);
    }

    benefitRefs.current.forEach((benefit, index) => {
      if (benefit) {
        const benefitTrigger = ScrollTrigger.create({
          id: `product-benefit-${index}`, // Add unique ID for each benefit
          trigger: benefit,
          start: "top 85%",
          once: true,
        });

        gsap.fromTo(
          benefit,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            delay: 0.15 * index,
            scrollTrigger: benefitTrigger,
          }
        );

        createdTriggers.push(benefitTrigger);
      }
    });

    // Store created triggers for cleanup
    scrollTriggerInstances.current = createdTriggers;

    // Cleanup function - only kill the ScrollTrigger instances we created
    return () => {
      scrollTriggerInstances.current.forEach((trigger) => {
        if (trigger) trigger.kill();
      });
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <h2
          ref={titleRef}
          className="text-3xl md:text-4xl font-bold text-center mb-4"
        >
          The Street Lawyer Services Difference
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          We're committed to providing premium cannabis products with a focus on
          quality, education, and exceptional service.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {benefits.map((benefit, index) => (
            <div
              key={benefit.id}
              ref={(el) => (benefitRefs.current[index] = el)}
              className="bg-gray-50 p-6 rounded-lg flex items-start"
            >
              <div className="mr-4 text-cannabis-green-500 flex-shrink-0">
                {benefit.icon}
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                <p className="text-gray-700">{benefit.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProductBenefits;
